import React, { useEffect, useState, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import api from './api';
import config from './config';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import FarmRunner from './components/FarmRunner';
import FarmAdmin from './components/FarmAdmin';
import GccRunner from './components/GccRunner';

function groupsFromUser(user) {
  try {
    const groups = user.signInUserSession.idToken.payload['cognito:groups'];
    if (!groups) return [];
    if (Array.isArray(groups)) return groups;
    if (typeof groups === 'string') {
      return groups.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
  } catch (e) {
    return [];
  }
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [view, setView] = useState('dashboard');

  const refreshUser = useCallback(async () => {
    try {
      const current = await Auth.currentAuthenticatedUser();
      setUser(current);
    } catch (e) {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const signOut = async () => {
    await Auth.signOut();
    setUser(null);
  };

  const goToDashboard = () => {
    setView('dashboard');
    setSelectedFarm(null);
  };

  const openGccTests = () => {
    setSelectedFarm(null);
    setView('gcc');
  };

  if (checking) {
    return <div className="centered">Loading…</div>;
  }

  if (!user) {
    return <Login onSignedIn={refreshUser} />;
  }

  const username =
    (user.attributes && (user.attributes.email || user.username)) || user.username;
  const isOpsAdmin = groupsFromUser(user).includes('ops-admins');

  const openRunnerForFarm = (farmCode) => {
    setSelectedFarm(farmCode);
    setView('runner');
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 onClick={goToDashboard} style={{ cursor: 'pointer' }}>
            SMAX Scenario Tests
          </h1>
          {selectedFarm && (
            <button className="link-btn" onClick={goToDashboard}>
              ← Back to Dashboard
            </button>
          )}
          {isOpsAdmin && (
            <button className="link-btn" onClick={() => setView(view === 'admin' ? 'dashboard' : 'admin')}>
              {view === 'admin' ? 'Dashboard' : 'Manage Farms'}
            </button>
          )}
          <button className="link-btn tab-btn" onClick={openGccTests}>
            GCC SMAX Tests
          </button>
        </div>
        <div className="user-box">
          <span>{username}</span>
          <button className="link-btn" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>
      <main>
        {view === 'admin' ? (
          <FarmAdmin api={api} />
        ) : view === 'gcc' ? (
          <GccRunner api={api} pollIntervalMs={config.pollIntervalMs} />
        ) : selectedFarm ? (
          <FarmRunner 
            api={api} 
            pollIntervalMs={config.pollIntervalMs}
            initialFarm={selectedFarm}
          />
        ) : (
          <Dashboard api={api} onSelectFarm={openRunnerForFarm} />
        )}
      </main>
    </div>
  );
}
